import './ScannedItem.css'
import { clsx } from 'clsx';
import step1Img from '../../images/return-step1.png'
import step2Img from '../../images/return-step2.png'
import step3Img from '../../images/return-step3.png'
import step4Img from '../../images/return-step4.png'
import GoogleMapReact from 'google-map-react'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'
import { useAPI } from '../../api/api'
import { LocationData } from '../../components/admin/LocationCard'
import ErrorElement from '../../components/ErrorElement'
import { LocationCoords } from '../customer/ReturnItemLocation'
import { MapMarker } from '../customer/ReturnMap'
import { ReactComponent as ArrowRightIcon } from '../../icons/arrow-right.svg'
import { ToSPPLabel } from '../../components/common/TosPPLabels'
import { LanguageSelect } from '../../components/LanguageSelector'
import UserLocationMarker from './UserLocationMarker';


// Haversine formula — returns distance in metres between two lat/lng points
const calculateDistance = (lat1: number, lon1: number, lat2: number, lon2: number): number => {
  const R = 6371e3
  const radLat1 = (lat1 * Math.PI) / 180
  const radLat2 = (lat2 * Math.PI) / 180
  const deltaLat = ((lat2 - lat1) * Math.PI) / 180
  const deltaLon = ((lon2 - lon1) * Math.PI) / 180
  const a =
    Math.sin(deltaLat / 2) * Math.sin(deltaLat / 2) +
    Math.cos(radLat1) * Math.cos(radLat2) * Math.sin(deltaLon / 2) * Math.sin(deltaLon / 2)
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a))
  return Math.round(R * c)
}

function useDragScroll() {
  const ref = useRef<HTMLDivElement>(null)
  const isDragging = useRef(false)
  const startX = useRef(0)
  const scrollLeft = useRef(0)
  const snapTimeout = useRef<ReturnType<typeof setTimeout>>()

  const onMouseDown = useCallback((e: React.MouseEvent) => {
    const el = ref.current
    if (!el) return
    // Clear any pending snap re-enable
    if (snapTimeout.current) clearTimeout(snapTimeout.current)
    isDragging.current = true
    startX.current = e.pageX - el.offsetLeft
    scrollLeft.current = el.scrollLeft
    el.style.scrollSnapType = 'none'
    el.style.scrollBehavior = 'auto'
  }, [])

  const onMouseMove = useCallback((e: React.MouseEvent) => {
    if (!isDragging.current) return
    e.preventDefault()
    const el = ref.current
    if (!el) return
    const x = e.pageX - el.offsetLeft
    const walk = (x - startX.current) * 1.2
    el.scrollLeft = scrollLeft.current - walk
  }, [])

  const onMouseUpOrLeave = useCallback(() => {
    if (!isDragging.current) return
    isDragging.current = false
    const el = ref.current
    if (!el) return
    // Delay re-enabling snap so the scroll can coast briefly,
    // then use smooth behaviour so the final snap isn't jarring.
    snapTimeout.current = setTimeout(() => {
      el.style.scrollBehavior = 'smooth'
      el.style.scrollSnapType = 'x mandatory'
    }, 80)
  }, [])

  return { ref, onMouseDown, onMouseMove, onMouseUp: onMouseUpOrLeave, onMouseLeave: onMouseUpOrLeave }
}

const ScannedItem = () => {
  const { t } = useTranslation()
  const { id }: { id: string } = useParams()

  const defaultLocation = { latitude: 45.5017, longitude: -73.5673 }
  const defaultZoomLvl = 14
  const focusedZoomLvl = 15

  const { errors, setErrors, wrappedExtendedQuery, currentPosition, locationPermissionDenied, watchPosition, clearWatchPosition } = useAPI()

  const [locations, setLocations] = useState<LocationData[]>([])
  const [item, setItem] = useState<any>()
  const [showMap, setShowMap] = useState(false)
  const [zoomLvl, setZoomLvl] = useState(defaultZoomLvl)
  const [selectedLocation, setSelectedLocation] = useState<LocationCoords>()

  const locationsRef = useRef<LocationData[]>([])
  const hasInitializedMap = useRef(false)
  const googleMapRef = useRef<any>(null)

  const mapRef = useRef<HTMLDivElement>(null)
  const dragScroll = useDragScroll()

  useEffect(() => {
    watchPosition()
    return () => clearWatchPosition()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    async function fetchItem() {
      const query = `query {itemsByIdOpen (filter : {uuid : "${id}"}) { _id uuid status itemType { name depositPrice } merchant{name} merchantCoordinates{latitude longitude}}}`
      const factory = (result: any): any => {
        return Array.isArray(result.itemsByIdOpen) && result.itemsByIdOpen.length > 0
          ? result.itemsByIdOpen.map((o: any) => ({
              _id: o._id,
              itemTypeName: o.itemType.name,
              depositPrice: o.itemType.depositPrice / 100,
              uuid: o.uuid,
              status: o.status,
              merchant: o.merchant ? o.merchant.name : null,
              merchantCoordinates: o.merchantCoordinates || null,
            }))[0]
          : null
      }

      const response = await wrappedExtendedQuery(query, factory)
      if (response) {
        setItem(response)
      } else {
        setErrors([{ msg: t('item-does-not-exist') }])
      }
    }
    fetchItem()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Fetch locations on mount (independent of item)
  useEffect(() => {
    async function fetchLocations() {
      const query = `query {dropOffLocationsOpen(filter: {status: "active"}) {_id name status capacity quantity address workTime website coordinates { latitude longitude } }}`
      const factory = (result: any): any => {
        return result.dropOffLocationsOpen.map((l: any) => ({
          ...l,
          freeCapacity: l.capacity - l.quantity,
        }))
      }

      const response = await wrappedExtendedQuery(query, factory)

      if (response) {
        locationsRef.current = response
        setShowMap(true)
      }
    }
    fetchLocations()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Re-sort locations by distance whenever position changes (without re-fetching or resetting zoom)
  useEffect(() => {
    const rawLocations = locationsRef.current
    if (!rawLocations.length) return

    const referencePoint = currentPosition
      ? { latitude: currentPosition.latitude, longitude: currentPosition.longitude }
      : item?.merchantCoordinates?.latitude && item?.merchantCoordinates?.longitude
        ? { latitude: item.merchantCoordinates.latitude, longitude: item.merchantCoordinates.longitude }
        : null

    let sorted = rawLocations.map((l: any) => ({
      ...l,
      distance: referencePoint
        ? calculateDistance(referencePoint.latitude, referencePoint.longitude, l.coordinates.latitude, l.coordinates.longitude)
        : null,
    }))

    if (referencePoint) {
      sorted = [...sorted].sort((a: any, b: any) => (a.distance ?? Infinity) - (b.distance ?? Infinity))
    }

    // Center the map on the closest location the first time we have a
    // definitive position (either granted or denied). If we don't have
    // currentPosition yet AND the permission hasn't been denied, wait
    // for the next update so we don't lock in the default too early.
    if (!hasInitializedMap.current) {
      // We have the user's real position — center on nearest location
      if (currentPosition && sorted.length > 0) {
        hasInitializedMap.current = true
        setSelectedLocation({
          latitude: sorted[0].coordinates.latitude,
          longitude: sorted[0].coordinates.longitude,
        })
        setZoomLvl(focusedZoomLvl)
      }
      // No user position and permission was denied — fall back to
      // merchant coords (already used as referencePoint) or default
      else if (locationPermissionDenied) {
        hasInitializedMap.current = true
        if (referencePoint && sorted.length > 0) {
          setSelectedLocation({
            latitude: sorted[0].coordinates.latitude,
            longitude: sorted[0].coordinates.longitude,
          })
          setZoomLvl(focusedZoomLvl)
        } else {
          setSelectedLocation({
            latitude: defaultLocation.latitude,
            longitude: defaultLocation.longitude,
          })
          setZoomLvl(defaultZoomLvl)
        }
      }
      // else: still waiting for permission prompt — don't lock in yet
    }

    setLocations(sorted)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPosition, showMap, locationPermissionDenied])

  const handleZoomChange = (zoom: number) => {
    setZoomLvl(zoom)
  }

  const panTo = (lat: number, lng: number) => {
    const map = googleMapRef.current
    if (map) {
      map.panTo({ lat, lng })
      map.setZoom(focusedZoomLvl)
    }
  }

  const steps = [
    { step: 1, image: step1Img, text: t('scanned-item-new.step1'), containImage: false },
    { step: 2, image: step2Img, text: t('scanned-item-new.step2'), containImage: true },
    { step: 3, image: step3Img, text: t('scanned-item-new.step3'), containImage: true },
    { step: 4, image: step4Img, text: t('scanned-item-new.step4'), containImage: false },
  ]

  return (
    <main className="scanned-item">
      <section className='scanned-item__language-wrapper'>
        <LanguageSelect />
      </section>
      <section className="scanned-item__header">
        <h1>{t('scanned-item-new.title')}</h1>
        <p>
          {t('scanned-item-new.subtitle-prefix')}
          <span className="highlight">{t('scanned-item-new.subtitle-highlight')}</span>
          {t('scanned-item-new.subtitle-suffix')}
        </p>
      </section>

      <ErrorElement errors={errors} className="centered" />

      <section
        className="scanned-item__steps"
        ref={dragScroll.ref}
        onMouseDown={dragScroll.onMouseDown}
        onMouseMove={dragScroll.onMouseMove}
        onMouseUp={dragScroll.onMouseUp}
        onMouseLeave={dragScroll.onMouseLeave}
      >
        <div className="scanned-item__steps-track">
          {steps.map(({ step, image, text, containImage }) => (
            <article className="scanned-item__step-card" key={step}>
              <div className="scanned-item__step-card-top">
                <div className="scanned-item__step-card-header">
                  <h3>
                    {t('scanned-item-new.step-label')} {step}
                  </h3>
                </div>
                <div className={clsx('scanned-item__step-card-image', {'scanned-item__step-card-image--contain': containImage})}>
                  <img src={image} alt={`${t('scanned-item-new.step-label')} ${step}`} draggable={false} />
                </div>
              </div>
              <div className="scanned-item__step-card-body">
                <ArrowRightIcon className="arrow" />
                <p>{text}</p>
              </div>
            </article>
          ))}
        </div>
      </section>

      {showMap && (
        <section className="scanned-item__map-section">
          <h2>{t('scanned-item-new.find-return-box')}</h2>

          {locationPermissionDenied && (
            <p className="scanned-item__location-denied-msg">{t('location-permission-denied-message')}</p>
          )}

          <div className="scanned-item__map-container" ref={mapRef}>
            <div className="scanned-item__map-btns">
              {currentPosition && (
                <button
                  className="scanned-item__locate-btn"
                  onClick={() => panTo(currentPosition.latitude, currentPosition.longitude)}
                  aria-label={t('locate-me')}
                >
                  <span className="scanned-item__locate-btn-icon" />
                </button>
              )}
              {(locations.length > 0 && currentPosition) && (
                <button
                  className="scanned-item__locate-btn"
                  onClick={() => panTo(locations[0].coordinates.latitude, locations[0].coordinates.longitude)}
                  aria-label={t('nearest-dropoff')}
                >
                  <svg className="scanned-item__dropoff-btn-icon" xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="var(--color-primary)" stroke="var(--color-primary)" strokeWidth="1">
                    <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5S10.62 6.5 12 6.5s2.5 1.12 2.5 2.5S13.38 11.5 12 11.5z"/>
                  </svg>
                </button>
              )}
            </div>
            <GoogleMapReact
              bootstrapURLKeys={{ key: process.env.REACT_APP_GMAPS_KEY || '' }}
              defaultCenter={{
                lat: defaultLocation.latitude,
                lng: defaultLocation.longitude,
              }}
              defaultZoom={defaultZoomLvl}
              center={{
                lat: selectedLocation?.latitude || defaultLocation.latitude,
                lng: selectedLocation?.longitude || defaultLocation.longitude,
              }}
              zoom={zoomLvl}
              onZoomAnimationStart={(zoom: number) => handleZoomChange(zoom)}
              yesIWantToUseGoogleMapApiInternals
              onGoogleApiLoaded={({ map }: { map: any }) => {
                googleMapRef.current = map
              }}
              options={{
                gestureHandling: 'greedy',
                zoomControl: false,
                fullscreenControl: false,
                mapTypeControl: false,
                streetViewControl: false,
                rotateControl: false,
                scaleControl: false,
              }}
            >
              {currentPosition && (
                <UserLocationMarker
                  key="user"
                  text="user"
                  lat={currentPosition.latitude}
                  lng={currentPosition.longitude}
                />
              )}
              {locations.map((location) => (
                <MapMarker
                  key={location._id}
                  lat={location.coordinates.latitude}
                  lng={location.coordinates.longitude}
                  text={location.name}
                  zoomLvl={zoomLvl}
                />
              ))}
            </GoogleMapReact>
          </div>
        </section>
      )}

      <section className="scanned-item__help">
        <p>{t('scanned-item-new.need-help')}</p>
        <a href="https://www.bopaq.com/contact" target="_blank" rel="noopener noreferrer">
          {t('scanned-item-new.contact-link')}
        </a>
      </section>

      <footer className="scanned-item__footer">
        <div className="scanned-item__footer-brand">
          <img src="/bopaq_logo.png" alt="bopaq logo" />
          <span>Bopaq</span>
        </div>
        <div className="scanned-item__footer-links">
          <ToSPPLabel hideButton />
        </div>
      </footer>
    </main>
  )
}

export { ScannedItem as default }
